/* eslint-disable @typescript-eslint/no-explicit-any */

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

function escapeXml(unsafe: any): string {
  if (unsafe == null) return ''
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function toLocalDate(dateLike: string | number | Date | null | undefined): Date | null {
  if (dateLike == null) return null
  if (dateLike instanceof Date) {
    return new Date(dateLike.getFullYear(), dateLike.getMonth(), dateLike.getDate())
  }
  if (typeof dateLike === 'number') {
    const dt = new Date(dateLike)
    if (isNaN(dt.getTime())) return null
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
  }
  const s = String(dateLike).trim()
  if (!s) return null

  // 1. Formato YYYYMMDD ou DDMMAAAA
  const m8 = s.match(/^(\d{4})(\d{2})(\d{2})$/)
  if (m8) {
    const p1 = Number(m8[1])
    const p2 = Number(m8[2])
    const p3 = Number(m8[3])
    if (p1 >= 1900 && p1 <= 2100 && p2 >= 1 && p2 <= 12 && p3 >= 1 && p3 <= 31) {
      return new Date(p1, p2 - 1, p3)
    }
  }

  const m8br = s.match(/^(\d{2})(\d{2})(\d{4})$/)
  if (m8br) {
    const d = Number(m8br[1])
    const mo = Number(m8br[2])
    const y = Number(m8br[3])
    if (y >= 1900 && y <= 2100 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return new Date(y, mo - 1, d)
    }
  }

  // 2. Formato YYYY-MM-DD
  const mIso = s.match(/^(\d{4})[-_.](\d{2})[-_.](\d{2})/)
  if (mIso) {
    return new Date(Number(mIso[1]), Number(mIso[2]) - 1, Number(mIso[3]))
  }

  // 3. Formato DD/MM/YYYY
  const mBr = s.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})/)
  if (mBr) {
    return new Date(Number(mBr[3]), Number(mBr[2]) - 1, Number(mBr[1]))
  }

  const parsed = new Date(s)
  if (isNaN(parsed.getTime())) return null
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

function getDayStatus(
  hasFile: boolean,
  justification: any,
  isSunday: boolean,
  isPast: boolean,
): 'enviado' | 'justificado' | 'domingo' | 'atraso' | 'none' {
  if (hasFile) return 'enviado'
  if (justification) return 'justificado'
  if (isSunday) {
    if (isPast) return 'domingo'
    return 'none'
  }
  if (isPast) return 'atraso'
  return 'none'
}

export interface ExportRegularidadeParams {
  distribuidores: { id: string; name: string; status?: string }[]
  files: any[]
  estoqueDb: any[]
  estoqueMesFiltered: any[]
  justify: any[]
  selectedMonth: number // 0 a 11
  selectedYear: number
  distributorId?: string | null
}

export function exportRegularidadeExcel({
  distribuidores,
  files,
  estoqueDb,
  estoqueMesFiltered,
  justify,
  selectedMonth,
  selectedYear,
  distributorId,
}: ExportRegularidadeParams): void {
  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate()
  const daysList = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1
    const dObj = new Date(selectedYear, selectedMonth, day)
    return {
      day,
      weekDay: WEEK_DAYS[dObj.getDay()],
    }
  })

  // Filtra distribuidores ativos ou compatíveis com a tela
  const filteredDistribuidores = (!distributorId
    ? distribuidores
    : distribuidores.filter((d) => String(d.id) === String(distributorId))
  ).filter((item: any) => {
    const status = item.status !== ' ' ? item.status : '1'
    if (status !== '0') return true

    const hasFilesThisMonth = files.some(
      (fi) => String(fi.dist_id ?? fi.distribuidor_id ?? '') === String(item.id),
    )
    const hasEstoqueDbThisMonth = estoqueDb.some(
      (e) => String(e.dist_id) === String(item.id),
    )
    const hasEstoqueMes = estoqueMesFiltered.some(
      (em) => String(em.dist_id) === String(item.id),
    )
    return hasFilesThisMonth || hasEstoqueDbThisMonth || hasEstoqueMes
  })

  const mesNome = MONTH_NAMES[selectedMonth] ?? `Mes_${selectedMonth + 1}`
  const today = new Date()

  // Montagem das Linhas
  const rowsXml: string[] = []

  // Linha 1: Título Geral do Relatório
  const totalCols = 2 + daysInMonth + 4
  rowsXml.push(`
   <Row ss:Height="30">
    <Cell ss:MergeAcross="${totalCols - 1}" ss:StyleID="ReportTitle">
     <Data ss:Type="String">RELATÓRIO DE REGULARIDADE DE ENVIOS - ${escapeXml(mesNome.toUpperCase())} / ${selectedYear}</Data>
    </Cell>
   </Row>
  `)

  // Linha 2: Subtítulo com Legenda
  rowsXml.push(`
   <Row ss:Height="22">
    <Cell ss:MergeAcross="${totalCols - 1}" ss:StyleID="SubTitle">
     <Data ss:Type="String">Legenda: [Verde = Enviado]  [Vermelho = Pendente]  [Laranja = Justificado]  [Cinza = Domingo]  [- = Futuro]</Data>
    </Cell>
   </Row>
  `)

  // Linha 3: Cabeçalho das Colunas
  const headerCellsXml: string[] = []
  headerCellsXml.push(`    <Cell ss:StyleID="HeaderMain"><Data ss:Type="String">Distribuidor</Data></Cell>`)
  headerCellsXml.push(`    <Cell ss:StyleID="HeaderMain"><Data ss:Type="String">Tipo</Data></Cell>`)

  daysList.forEach(({ day, weekDay }) => {
    const label = `${String(day).padStart(2, '0')} (${weekDay})`
    headerCellsXml.push(`    <Cell ss:StyleID="HeaderDay"><Data ss:Type="String">${label}</Data></Cell>`)
  })

  headerCellsXml.push(`    <Cell ss:StyleID="HeaderSummary"><Data ss:Type="String">Enviados</Data></Cell>`)
  headerCellsXml.push(`    <Cell ss:StyleID="HeaderSummary"><Data ss:Type="String">Pendentes</Data></Cell>`)
  headerCellsXml.push(`    <Cell ss:StyleID="HeaderSummary"><Data ss:Type="String">Justificados</Data></Cell>`)
  headerCellsXml.push(`    <Cell ss:StyleID="HeaderSummary"><Data ss:Type="String">% Regularidade</Data></Cell>`)

  rowsXml.push(`
   <Row ss:Height="26">
${headerCellsXml.join('\n')}
   </Row>
  `)

  // Dados dos Distribuidores
  filteredDistribuidores.forEach((item) => {
    const distEstoqueFiles = files.filter((fi) => {
      const fileDate = toLocalDate(fi.envio || fi.created_at || fi.dt_envio)
      if (!fileDate) return false
      const nm = String(fi.nm_file || '').toLowerCase()
      const isEstoque = nm.includes('estoque') || nm.includes('esto') || nm.startsWith('est')
      return (
        String(fi.dist_id ?? fi.distribuidor_id ?? '') === String(item.id) &&
        isEstoque &&
        fileDate.getMonth() === selectedMonth &&
        fileDate.getFullYear() === selectedYear
      )
    })

    const distEstoqueDb = estoqueDb.filter((e) => String(e.dist_id) === String(item.id))

    const maxEstoqueDayFromFiles = distEstoqueFiles.reduce((max, fi) => {
      const d = toLocalDate(fi.envio || fi.created_at || fi.dt_envio)
      if (!d || d.getMonth() !== selectedMonth || d.getFullYear() !== selectedYear) return max
      const day = d.getDate()
      return day > max ? day : max
    }, 0)

    const maxEstoqueDayFromDb = distEstoqueDb.reduce((max, e) => {
      const d = toLocalDate(e.dt_ref || e.created_at)
      if (!d || d.getMonth() !== selectedMonth || d.getFullYear() !== selectedYear) return max
      const day = d.getDate()
      return day > max ? day : max
    }, 0)

    const isCurrentMonth =
      selectedMonth === today.getMonth() && selectedYear === today.getFullYear()
    const isFutureMonth =
      selectedYear > today.getFullYear() ||
      (selectedYear === today.getFullYear() && selectedMonth > today.getMonth())

    let maxEstoqueDay = isFutureMonth
      ? 0
      : Math.max(maxEstoqueDayFromFiles, maxEstoqueDayFromDb)

    if (isCurrentMonth) {
      maxEstoqueDay = Math.min(maxEstoqueDay, today.getDate())
    }

    // --- LINHA ESTOQUE ---
    let estoqueEnviados = 0
    let estoquePendentes = 0
    let estoqueJustificados = 0
    let estoqueDiasUteis = 0

    const estoqueCellsXml: string[] = []
    estoqueCellsXml.push(`    <Cell ss:StyleID="DistName"><Data ss:Type="String">${escapeXml(item.name)}</Data></Cell>`)
    estoqueCellsXml.push(`    <Cell ss:StyleID="TipoBadge"><Data ss:Type="String">Estoque</Data></Cell>`)

    daysList.forEach(({ day }) => {
      const todayDate = new Date()
      const dayDate = new Date(selectedYear, selectedMonth, day)
      const isPast =
        dayDate <
        new Date(
          todayDate.getFullYear(),
          todayDate.getMonth(),
          todayDate.getDate(),
        )
      const isSunday = dayDate.getDay() === 0

      const justificationForDay = justify.find((j) => {
        const refDate = toLocalDate(j.dt_referencia)
        return (
          String(j.dist_id) === String(item.id) &&
          refDate &&
          refDate.getDate() === day &&
          refDate.getMonth() === selectedMonth &&
          refDate.getFullYear() === selectedYear
        )
      })

      const isEstoqueCovered = maxEstoqueDay > 0 && day <= maxEstoqueDay
      const status = getDayStatus(
        isEstoqueCovered,
        justificationForDay,
        isSunday,
        isPast,
      )

      if (isPast && !isSunday) {
        estoqueDiasUteis++
      }

      if (status === 'enviado') {
        estoqueEnviados++
        estoqueCellsXml.push(`    <Cell ss:StyleID="StatusEnviado"><Data ss:Type="String">Enviado</Data></Cell>`)
      } else if (status === 'atraso') {
        estoquePendentes++
        estoqueCellsXml.push(`    <Cell ss:StyleID="StatusAtraso"><Data ss:Type="String">Pendente</Data></Cell>`)
      } else if (status === 'justificado') {
        estoqueJustificados++
        estoqueCellsXml.push(`    <Cell ss:StyleID="StatusJustificado"><Data ss:Type="String">Justificado</Data></Cell>`)
      } else if (status === 'domingo') {
        estoqueCellsXml.push(`    <Cell ss:StyleID="StatusDomingo"><Data ss:Type="String">Domingo</Data></Cell>`)
      } else {
        estoqueCellsXml.push(`    <Cell ss:StyleID="StatusNeutro"><Data ss:Type="String">-</Data></Cell>`)
      }
    })

    const estoquePct =
      estoqueDiasUteis > 0
        ? Math.round(((estoqueEnviados + estoqueJustificados) / estoqueDiasUteis) * 100)
        : 100

    estoqueCellsXml.push(`    <Cell ss:StyleID="SummaryNumber"><Data ss:Type="Number">${estoqueEnviados}</Data></Cell>`)
    estoqueCellsXml.push(`    <Cell ss:StyleID="SummaryNumberAtraso"><Data ss:Type="Number">${estoquePendentes}</Data></Cell>`)
    estoqueCellsXml.push(`    <Cell ss:StyleID="SummaryNumberJust"><Data ss:Type="Number">${estoqueJustificados}</Data></Cell>`)
    estoqueCellsXml.push(`    <Cell ss:StyleID="SummaryNumber"><Data ss:Type="String">${estoquePct}%</Data></Cell>`)

    rowsXml.push(`
   <Row ss:Height="20">
${estoqueCellsXml.join('\n')}
   </Row>
    `)

    // --- LINHA SELLOUT ---
    let selloutEnviados = 0
    let selloutPendentes = 0
    let selloutJustificados = 0
    let selloutDiasUteis = 0

    const selloutCellsXml: string[] = []
    selloutCellsXml.push(`    <Cell ss:StyleID="DistName"><Data ss:Type="String">${escapeXml(item.name)}</Data></Cell>`)
    selloutCellsXml.push(`    <Cell ss:StyleID="TipoBadge"><Data ss:Type="String">Sellout</Data></Cell>`)

    daysList.forEach(({ day }) => {
      const todayDate = new Date()
      const dayDate = new Date(selectedYear, selectedMonth, day)
      const isPast =
        dayDate <
        new Date(
          todayDate.getFullYear(),
          todayDate.getMonth(),
          todayDate.getDate(),
        )
      const isSunday = dayDate.getDay() === 0

      const justificationForDay = justify.find((j) => {
        const refDate = toLocalDate(j.dt_referencia)
        return (
          String(j.dist_id) === String(item.id) &&
          refDate &&
          refDate.getDate() === day &&
          refDate.getMonth() === selectedMonth &&
          refDate.getFullYear() === selectedYear
        )
      })

      const selloutFile = files.find((fi) => {
        const fileDate = toLocalDate(fi.envio || fi.created_at || fi.dt_envio)
        if (!fileDate) return false
        const nm = String(fi.nm_file || '').toLowerCase()
        const isSellout =
          (nm.includes('sellout') ||
            nm.includes('vendas') ||
            nm.includes('venda')) &&
          !nm.includes('forcavendas') &&
          !nm.includes('força_vendas') &&
          !nm.includes('forca_vendas')

        return (
          String(fi.dist_id ?? fi.distribuidor_id ?? '') === String(item.id) &&
          isSellout &&
          fileDate.getDate() === day &&
          fileDate.getMonth() === selectedMonth &&
          fileDate.getFullYear() === selectedYear
        )
      })

      const hasFileSellout = !!selloutFile
      const status = getDayStatus(
        hasFileSellout,
        justificationForDay,
        isSunday,
        isPast,
      )

      if (isPast && !isSunday) {
        selloutDiasUteis++
      }

      if (status === 'enviado') {
        selloutEnviados++
        selloutCellsXml.push(`    <Cell ss:StyleID="StatusEnviado"><Data ss:Type="String">Enviado</Data></Cell>`)
      } else if (status === 'atraso') {
        selloutPendentes++
        selloutCellsXml.push(`    <Cell ss:StyleID="StatusAtraso"><Data ss:Type="String">Pendente</Data></Cell>`)
      } else if (status === 'justificado') {
        selloutJustificados++
        selloutCellsXml.push(`    <Cell ss:StyleID="StatusJustificado"><Data ss:Type="String">Justificado</Data></Cell>`)
      } else if (status === 'domingo') {
        selloutCellsXml.push(`    <Cell ss:StyleID="StatusDomingo"><Data ss:Type="String">Domingo</Data></Cell>`)
      } else {
        selloutCellsXml.push(`    <Cell ss:StyleID="StatusNeutro"><Data ss:Type="String">-</Data></Cell>`)
      }
    })

    const selloutPct =
      selloutDiasUteis > 0
        ? Math.round(((selloutEnviados + selloutJustificados) / selloutDiasUteis) * 100)
        : 100

    selloutCellsXml.push(`    <Cell ss:StyleID="SummaryNumber"><Data ss:Type="Number">${selloutEnviados}</Data></Cell>`)
    selloutCellsXml.push(`    <Cell ss:StyleID="SummaryNumberAtraso"><Data ss:Type="Number">${selloutPendentes}</Data></Cell>`)
    selloutCellsXml.push(`    <Cell ss:StyleID="SummaryNumberJust"><Data ss:Type="Number">${selloutJustificados}</Data></Cell>`)
    selloutCellsXml.push(`    <Cell ss:StyleID="SummaryNumber"><Data ss:Type="String">${selloutPct}%</Data></Cell>`)

    rowsXml.push(`
   <Row ss:Height="20">
${selloutCellsXml.join('\n')}
   </Row>
    `)
  })

  // Largura das Colunas
  const columnsXml: string[] = []
  columnsXml.push(`   <Column ss:Width="220"/>`) // Distribuidor
  columnsXml.push(`   <Column ss:Width="70"/>`)  // Tipo
  for (let i = 0; i < daysInMonth; i++) {
    columnsXml.push(`   <Column ss:Width="56"/>`) // Dias
  }
  columnsXml.push(`   <Column ss:Width="72"/>`)  // Enviados
  columnsXml.push(`   <Column ss:Width="72"/>`)  // Pendentes
  columnsXml.push(`   <Column ss:Width="78"/>`)  // Justificados
  columnsXml.push(`   <Column ss:Width="88"/>`)  // % Regularidade

  const spreadsheetXml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
  <Author>GID Regularidade</Author>
  <Created>${new Date().toISOString()}</Created>
 </DocumentProperties>
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center"/>
   <Borders/>
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Color="#000000"/>
   <Interior/>
   <NumberFormat/>
   <Protection/>
  </Style>

  <!-- Títulos e Cabeçalhos -->
  <Style ss:ID="ReportTitle">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="14" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#00205B" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="SubTitle">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Italic="1" ss:Color="#E2E8F0"/>
   <Interior ss:Color="#0B2B6B" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="HeaderMain">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#00205B" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="HeaderDay">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#0F3B8C" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="HeaderSummary">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#00205B" ss:Pattern="Solid"/>
  </Style>

  <!-- Células de Dados -->
  <Style ss:ID="DistName">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#1E293B"/>
   <Interior ss:Color="#F8FAFC" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="TipoBadge">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#334155"/>
   <Interior ss:Color="#EDF2F7" ss:Pattern="Solid"/>
  </Style>

  <!-- Status Coloridos Solicitados -->
  <Style ss:ID="StatusEnviado">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#45CE01" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="StatusAtraso">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#D60606" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="StatusJustificado">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#FF8400" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="StatusDomingo">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#A7A7A7" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="StatusNeutro">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#E2E8F0"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Color="#94A3B8"/>
   <Interior ss:Color="#F8FAFC" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="SummaryNumber">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#00205B"/>
   <Interior ss:Color="#F1F5F9" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="SummaryNumberAtraso">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#D60606"/>
   <Interior ss:Color="#FFF1F2" ss:Pattern="Solid"/>
  </Style>

  <Style ss:ID="SummaryNumberJust">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CBD5E1"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="9" ss:Bold="1" ss:Color="#C2410C"/>
   <Interior ss:Color="#FFFBEB" ss:Pattern="Solid"/>
  </Style>
 </Styles>

 <Worksheet ss:Name="Regularidade">
  <Table>
${columnsXml.join('\n')}
${rowsXml.join('\n')}
  </Table>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <FreezePanes/>
   <FrozenNoSplit/>
   <SplitRow>3</SplitRow>
   <TopRowBottomPane>3</TopRowBottomPane>
   <SplitColumn>2</SplitColumn>
   <LeftColumnRightPane>2</LeftColumnRightPane>
   <ActivePane>0</ActivePane>
  </WorksheetOptions>
 </Worksheet>
</Workbook>`

  // Download do arquivo
  const blob = new Blob([spreadsheetXml], {
    type: 'application/vnd.ms-excel;charset=utf-8;',
  })
  const link = document.createElement('a')
  const fileName = `Regularidade_Envios_${mesNome}_${selectedYear}.xls`

  link.href = URL.createObjectURL(blob)
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}
